<script setup>
import { ref, watch } from 'vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useAttachmentLoadState } from 'dashboard/composables/useAttachmentLoadState';
import { useMessageContext } from '../provider.js';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const props = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
});

const showGallery = ref(false);
const hasLoadError = ref(false);
const isLoaded = ref(false);
const playerKey = ref(0);

const { filteredCurrentChatAttachments, createdAt } = useMessageContext();
const { hasFailed, markLoadFailure, reset } = useAttachmentLoadState(createdAt);

const handleError = () => {
  hasLoadError.value = true;
  isLoaded.value = false;
  markLoadFailure();
};

// The file may not be in the storage yet when the message is first received. message.updated is
// sent once the file is uploaded: remount the <video>, since the same src is not requested again.
watch(
  () => props.attachment,
  () => {
    if (!hasLoadError.value) return;

    reset();
    hasLoadError.value = false;
    playerKey.value += 1;
  }
);
</script>

<template>
  <div
    class="relative size-[72px] overflow-hidden contain-content rounded-xl cursor-pointer group"
    @click="showGallery = true"
  >
    <div
      v-if="hasFailed"
      class="flex flex-col items-center justify-center gap-1 text-xs text-center rounded-lg size-full bg-n-alpha-1 text-n-slate-11"
    >
      <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
      {{ $t('COMPONENTS.MEDIA.LOADING_FAILED') }}
    </div>
    <template v-else>
      <video
        :key="playerKey"
        :src="attachment.dataUrl"
        class="w-full h-full object-cover"
        muted
        playsInline
        @loadedmetadata="isLoaded = true"
        @error="handleError"
      />
      <div
        v-if="!isLoaded"
        data-testid="media-loading"
        class="absolute inset-0 flex items-center justify-center bg-n-alpha-1"
        :title="$t('COMPONENTS.MEDIA.LOADING')"
      >
        <Icon
          icon="i-lucide-loader-circle"
          class="size-5 text-n-slate-11 animate-spin"
        />
      </div>
      <div
        v-else
        class="absolute w-full h-full inset-0 p-1 flex items-center justify-center"
      >
        <div
          class="size-7 bg-n-slate-1/60 backdrop-blur-sm rounded-full overflow-hidden shadow-[0_5px_15px_rgba(0,0,0,0.4)]"
        >
          <Icon
            icon="i-teenyicons-play-small-solid"
            class="size-7 text-n-slate-12/80 backdrop-blur"
          />
        </div>
      </div>
    </template>
  </div>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="handleError"
    @close="() => (showGallery = false)"
  />
</template>
